/* eslint-disable @typescript-eslint/no-explicit-any */
import { PayloadAction } from '@reduxjs/toolkit'
import { takeEvery, take, all, put, call, select, debounce, race, takeLatest } from 'redux-saga/effects'
import { ParsedConfig, errorNotification } from '@carto/kepler.gl'
import { Layer } from '@carto/kepler.gl/layers'
import {
  ActionTypes,
  addNotification,
  addDataToMap,
  receiveMapConfig,
  AddDataToMapOptions,
  layerTypeChange,
  layerConfigChange,
  updateMap,
  addLayer as keplerAddLayerActionCreator,
  AddDataToMapPayload
} from '@carto/kepler.gl/actions'
import { RasterMetadata } from '@carto/api-client'
import { getLayerById } from './keplerGlSlice'
import { getCurrentMap, setCurrentMapInitialConfig, updateCurrentMap } from '../../maps/state/mapsSlice'
import { Formats, MapConfig } from 'features/maps/types'
import { Source } from '@carto/react-redux'
import { isEqual } from 'lodash'

import {
  showGlobalBuilderError,
  addLayerRequest,
  addQueryRequest,
  updateQueryRequest,
  cancelQueryRequest,
  refreshDatasetRequest,
  UpdateQueryProps,
  QueryActions,
  setIsLoading,
  addLayerFromImportedJobRequest,
  newWidget,
  removeWidget,
  removeWidgetsByDataSource,
  updateWidgetProps,
  replaceWidget,
  ADD_FILTER_ACTION,
  REMOVE_FILTER_ACTION,
  ADD_SPATIAL_FILTER_ACTION,
  REMOVE_SPATIAL_FILTER_ACTION,
  setWidgets,
  setLegendSettings,
  setLayerLegendSettings,
  setPopupSettings,
  setLayerPopupSettings,
  setLayerUpdateStatus,
  showSQLPanel,
  queryThisTableRequest,
  convertTableToQueryDatasetRequest,
  aggregateDatasetByGeometry,
  moveLayerPopupSettings,
  addSqlParameter,
  updateSqlParameter,
  removeSqlParameter,
  setViewerCredentialsRequired,
  attachViewerCredentials,
  refreshDatasetsByConnections,
  setSaveMapError,
  initBuilderMap,
  setDescriptionOpen,
  setControlsPaneOpen,
  setControlsPaneState,
  mapPasswordUpdated,
  setMapPasswordRequired,
  incMapPasswordEnterAttempt,
  setCurrentMapLockStatus,
  updateCurrentMapLockStatus,
  SET_GEOCODER_RESULT,
  setRightPaneTab,
  isAutosavingEnabledSelector,
  removeTimeSeriesDataSourceAnimating,
  setMapScreenshot,
  setSaveNeeded,
  resetSaveNeeded,
  setBuilderMode,
  setPreviewMode,
  addTimeSeriesDataSourceAnimating,
  mapConfigSelector,
  widgetsMapConfigSelector,
  updateAISettings,
  addLayerToExistingDatasetRequest,
  setLayerPanelLoadingStatus,
  duplicateDatasetRequest,
  getDataSourcesIncompatibleWithWidgets,
  updateBasemapConfig
} from './builderSlice'
import {
  DEFAULT_GEO_COLUMN,
  DEFAULT_LOAD_MAP_OPTIONS,
  NEW_MAP_STATE,
  BUILDER_MIN_FRACTIONAL_ZOOM,
  DEFAULT_AGGREGATION_LEVEL_HEATMAP,
  DEFAULT_AGGREGATION_LEVEL_CLUSTER,
  DEFAULT_AGGREGATION_EXP
} from '../ui/builderConstants'
import {
  deleteCurrentMapDataset,
  getDatasetById,
  setCurrentMap,
  setEditMapStatus,
  setFetchMapError,
  setLatestMapsStatus,
  updateMapSettings
} from 'features/maps/state/mapsSlice'
import {
  CartoKeplerMapConfig,
  Map,
  MapDataset,
  MapPrivacy,
  MapType,
  SqlParameter,
  SqlParameterType
} from 'features/maps/types'
import { createMap, deleteMapDataset, lockMap, patchDataset } from 'features/maps/api/maps'
import { KeplerGlSchema } from '@carto/kepler.gl'
import { LayerRequest, NoSpatialIndex, SpatialIndex } from '..'
import {
  refreshDataset,
  runDatasetTransaction,
  updateDatasetColumnsFromMap,
  updateDatasetsColumns,
  getDatasetSupportedLayerClasses
} from './datasetSagas'
import { QueryLayerLoader, TilesetLayerLoader } from './loaders'
import { createMapThumbnail, saveMapThumbnail } from './thumbnailsSagas'
import { getDatasetLabel, getNextLayerLabel } from './labels'
import { RequestStatus } from 'features/common'
import { updateCurrentMapDataset } from 'features/maps/state/mapsSlice'
import { DataExplorerTable, TreeObjectType } from 'features/data-explorer/types'
import { getDataExplorerObject } from 'features/data-explorer/api/dataExplorer'
import { ImportingJob } from 'features/jobs/types'
import { Connection, ConnectionPrivacy } from 'features/connections'
import {
  loadDataForWidgets,
  loadWidgetsMapConfig,
  removeDataForWidgets,
  removeCrossFilteringWidgetFilters,
  updateCrossFilteringWidgets,
  updateWidgetsGivenDatasetChanges
} from './WidgetsSagas'
import { loadBasemapsSettings } from './baseMapsSagas'
import { loadLegendSettings } from './legendSagas'
import { loadPopupSettings, updatePopupSettingsWithValidation } from './popupSagas'
import {
  getConvertDatasetToQueryIncompatibleSettings,
  getFilterByWidget,
  getGeoColumnFromTable,
  getGeoColumnSpecifier,
  getDatasetSettingsForTable,
  isLayerTypeCompatibleWithQuery,
  getLayerTypeChangeIncompatibleSettings,
  findFirstSuitableLayerConfigByDataset,
  getBoundsFromTilesetDataset,
  getSchemaFromTilesetDataset
} from './builderSliceUtils'
import { MetricEventType } from 'features/metrics/api/metrics'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'

import { loadSqlParametersFromMap } from './queryFiltersSagas'
import { User, UserRole } from 'features/users'
import {
  setDefaultMapConfig,
  setViewerDefaultMapConfig,
  setViewerDefaultSearch,
  setViewerDefaultSqlParameters,
  userCanEditMap,
  userCanViewMap
} from 'features/maps/utils'
import { RootState } from 'app/state/store'
import { getStore } from 'app/state/storeRegistry'

import { askQuestionWithDialog } from 'features/dialogs/askQuestionWithDialog'
import { connectionsQuery } from 'features/connections/api/connectionsQueries'
import {
  BuilderMode,
  KeplerTable,
  MapDatasetEnriched,
  InitBuilderRequestParams,
  WidgetType,
  BuilderLockState,
  WidgetsMapConfig,
  LayerTypes,
  KeplerLayer,
  LayerType,
  KeplerTableMetadata,
  DistanceUnits,
  BUILDER_MEASURE_TOOL_UNIT_KEY,
  Widget
} from '../types'
import { LayerLoader } from './loaders/layerLoader'
import { fetchQueryEffect } from 'queryClient'
import { usersMeQuery } from 'features/users/api/userQueries'
import {
  getConnectionsWithMissingViewerCredentials,
  shouldRequireViewerCredentials,
  shouldRequireViewerCredentialsForGuest
} from './viewerCredentials'
import { QuotaResult, QuotaTypes } from 'features/quotas/types'
import { getFlag, setStatsStatus } from 'features/account/state/accountSlice'
import { createApiError, isApiError } from 'features/common/api/apiError'
import { getQuota } from 'features/quotas/state/quotaSagas'
import { assertAddditionWouldNotExceedQuota, isHardQuotaError } from 'features/quotas/utils'
import { loadPersistentUiStatePreLoad, loadPersistentUiStatePostLoad } from './uiState'
import { FeatureFlag } from 'app/types'
import {
  datasetRequiresColumns,
  datasetSchemaEqual,
  getDatasetDefaultColumns,
  getNewDatasetColor,
  isWorkflowDataset
} from './datasetUtils'
import { getEnrichedDatasetById, getkeplerDatasetById } from './keplerGlSlice'
import { fetchMapDetails, saveCurrentMap } from 'features/maps/state/mapsSagas'
import { AnyUrlSearchParams, navigateWithReload } from 'features/common/utils/navigationUtils'
import { BuilderSearchParam, MAX_URL_LENGTH } from 'features/common/utils/searchParamsUtils'
import { getDefaultMapConfigFromSearchParams, safeWriteWKT } from 'features/builder/utils/searchParamsUtils'
import { getDefaultAggregationExpForLayerType } from './spatialIndexUtils'
import { layerTypeChangesIncompatibleUserPrompt, removeIncompatibleMapFeatures } from '../model/layerDataset'
import { getValidatedFilters, migrateSpatialIndexFilters } from './widgetsUtils'
import { loadAttributeStatsForDynamicTiling } from './statsSagas'
import { migrateDocumentModeLayers } from '../model/documentModeMigration'
import { migrateGenAiToAgent } from '../model/genAiMigrationToAgent'
import { fitBounds, isLatitude, isLongitude } from '../utils/mapUtils'
import { clamp } from 'features/common/utils/clamp'
import { MAXIMUM_ZOOM_LEVEL } from 'features/common/ui/Map/ZoomControl'
import { setCommentsVisible, updateActiveComment } from './mapCommentsSlice'
import { syncCommentFromBackend } from '../model/mapCommentsSync'
import { centerMapOnComment } from '../ui/Comments/utils'
import { getLocalStorageItem } from 'app/api/localStorage'
import { isAgentDataset } from '../model/agent'
import { ParentAppMapUpdatedEventData, sendParentAppEvent } from 'features/builder/parentAppEvent'
import { isInIframe } from 'app/utils/embedding'
import { appStoreDispatch, appStoreSelect } from 'app/state/storeUtils'

const LayerTypeChangeConfirmDialogLazy = () =>
  import('../ui/KeplerGl/SidePanel/LayerPanel/LayerTypeChangeConfirmDialog')

const LayerAggregateByGeometryConfirmDialogLazy = () =>
  import('../ui/KeplerGl/SidePanel/LayerPanel/LayerAggregateByGeometryConfirmDialog')

const actionsListForAutoSaving = [
  ActionTypes.MAP_CONFIG_CHANGE,
  ActionTypes.UPDATE_MAP,
  ActionTypes.INTERACTION_CONFIG_CHANGE,
  ActionTypes.LAYER_CONFIG_CHANGE,
  ActionTypes.LAYER_TYPE_CHANGE,
  ActionTypes.LAYER_VIS_CONFIG_CHANGE,
  ActionTypes.LAYER_VISUAL_CHANNEL_CHANGE,
  ActionTypes.LAYER_COLOR_UI_CHANGE,
  ActionTypes.LAYER_TEXT_LABEL_CHANGE,
  ActionTypes.REMOVE_FILTER,
  ActionTypes.REMOVE_LAYER,
  ActionTypes.REORDER_LAYER,
  ActionTypes.SET_FILTER,
  ActionTypes.SET_FILTER_PLOT,
  ActionTypes.UPDATE_LAYER_BLENDING,
  ActionTypes.TOGGLE_PERSPECTIVE,
  ActionTypes.TOGGLE_SPLIT_MAP,
  ActionTypes.TOGGLE_LAYER_FOR_MAP,
  ActionTypes.DUPLICATE_LAYER,
  ActionTypes.REMOVE_DATASET,
  newWidget.type,
  removeWidget.type,
  setWidgets.type,
  removeWidgetsByDataSource.type,
  updateWidgetProps.type,
  replaceWidget.type,
  ADD_FILTER_ACTION,
  REMOVE_FILTER_ACTION,
  updateBasemapConfig.type,
  ADD_SPATIAL_FILTER_ACTION,
  REMOVE_SPATIAL_FILTER_ACTION,
  setLegendSettings.type,
  setLayerLegendSettings.type,
  setPopupSettings.type,
  setLayerPopupSettings.type,
  updateMapSettings.type,
  addSqlParameter.type,
  updateSqlParameter.type,
  removeSqlParameter.type,
  setDescriptionOpen.type,
  setControlsPaneOpen.type,
  setControlsPaneState.type,
  setRightPaneTab.type,
  setIsLoading.type,
  updateCurrentMapDataset.type,
  removeTimeSeriesDataSourceAnimating.type,
  updateAISettings.type,
  setCommentsVisible.type
]

const actionsListForCreateThumbnail = [
  ActionTypes.MAP_CONFIG_CHANGE,
  ActionTypes.UPDATE_MAP,
  ActionTypes.LAYER_CONFIG_CHANGE,
  ActionTypes.LAYER_TYPE_CHANGE,
  ActionTypes.LAYER_VIS_CONFIG_CHANGE,
  ActionTypes.LAYER_VISUAL_CHANNEL_CHANGE,
  ActionTypes.REMOVE_FILTER,
  ActionTypes.REMOVE_LAYER,
  ActionTypes.REORDER_LAYER,
  ActionTypes.SET_FILTER,
  ActionTypes.SET_FILTER_PLOT,
  ActionTypes.UPDATE_LAYER_BLENDING,
  ActionTypes.TOGGLE_PERSPECTIVE,
  ActionTypes.DUPLICATE_LAYER,
  ActionTypes.ADD_DATA_TO_MAP,
  ActionTypes.REMOVE_DATASET,
  ADD_FILTER_ACTION,
  REMOVE_FILTER_ACTION,
  updateBasemapConfig.type,
  ADD_SPATIAL_FILTER_ACTION,
  REMOVE_SPATIAL_FILTER_ACTION
]

const actionsListForUrlUpdate = [
  ActionTypes.LAYER_CONFIG_CHANGE,
  ActionTypes.REMOVE_FILTER,
  ActionTypes.SET_FILTER,
  ActionTypes.TOGGLE_LAYER_FOR_MAP,
  ActionTypes.UPDATE_MAP,
  ADD_FILTER_ACTION,
  REMOVE_FILTER_ACTION,
  ADD_SPATIAL_FILTER_ACTION,
  REMOVE_SPATIAL_FILTER_ACTION,
  SET_GEOCODER_RESULT,
  updateQueryRequest.type
]

export function* getMapConfig() {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  return yield select(mapConfigSelector)
}

export function* getNewMapConfig() {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  const mapConfig = yield getMapConfig()
  const measurementUnit = getLocalStorageItem(BUILDER_MEASURE_TOOL_UNIT_KEY) || DistanceUnits.Kilometers
  return {
    ...mapConfig,
    config: {
      ...mapConfig?.config,
      mapSettings: {
        ...mapConfig?.config.mapSettings,
        measurementUnit
      }
    }
  }
}

function* handleInitBuilderMap(action: PayloadAction<InitBuilderRequestParams>) {
  try {
    const { mapId: loadedMapId, createMapRequest, builderMode, searchParams } = action.payload

    const options: AddDataToMapOptions = { ...DEFAULT_LOAD_MAP_OPTIONS }
    if (builderMode !== BuilderMode.Editor) {
      options.readOnly = true
    }

    yield put(setIsLoading(true))

    let createdMapId: string | undefined
    if (!loadedMapId && createMapRequest) {
      yield loadBasemapsSettings()
      yield put(updateMap(NEW_MAP_STATE))
      const createdMap = yield createNewMap(createMapRequest.map)
      createdMapId = createdMap.id
    } else if (loadedMapId) {
      while (true) {
        const publicMapAuthorizationHeader = yield select((state) => state.builder.publicMapAuthorizationHeader)
        const fetchMapError = yield fetchMapDetails({
          id: loadedMapId,
          privacy: builderMode === BuilderMode.Editor ? MapPrivacy.Private : MapPrivacy.Public,
          headers:
            builderMode === BuilderMode.Public && publicMapAuthorizationHeader
              ? { Authorization: publicMapAuthorizationHeader }
              : undefined
        })

        if (isApiError(fetchMapError)) {
          if (
            builderMode !== BuilderMode.Editor &&
            fetchMapError?.status === 401 &&
            fetchMapError.error === 'Basic auth required'
          ) {
            yield put(setIsLoading(false))
            yield put(incMapPasswordEnterAttempt())
            yield put(setMapPasswordRequired(true))

            // wait for password update and continue
            yield take(mapPasswordUpdated.type)
            continue
          } else {
            console.error('initBuilderMap: failed to fetch map', fetchMapError)
            // let useLoadMap handle 404/403
            yield put(setFetchMapError(fetchMapError))
            return
          }
        } else {
          yield put(setMapPasswordRequired(false))
          break
        }
      }
      yield loadBasemapsSettings()
      yield fixupBrokenMapState()
      yield snapshotMapConfig()
      yield configureMapFromUrlParams({ searchParams })
    }

    if (!(yield checkMapAuthorization({ builderMode, searchParams }))) {
      return
    }

    if (builderMode === BuilderMode.Editor) {
      yield maybeAutoLockCollaborativeMap()
      if (loadedMapId) {
        yield addMissingPropertiesToOldDatasets()
      }
    }

    yield loadCurrentMap(options)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const me: User = yield select((state) => state.users.me)
    if (shouldRequireViewerCredentialsForGuest(currentMapDatasets, me)) {
      return false
    }

    if (createdMapId && createMapRequest && createMapRequest.layer) {
      yield call(addLayer, addLayerRequest({ ...createMapRequest.layer, mapId: createdMapId }))
    }

    const datasets = yield select((state) => state.maps.currentMapDatasets)
    for (const dataset of datasets) {
      yield updatePopupSettingsWithValidation(dataset.id)
    }
    if (loadedMapId && searchParams?.commentId) {
      const comment = yield syncCommentFromBackend(loadedMapId, searchParams.commentId, { store: getStore() })
      centerMapOnComment(comment.anchor, { store: getStore(), isMobile: false })
      yield put(updateActiveComment(comment))
    }
    if (isInIframe) {
      const widgets: Widget[] = yield select((state: RootState) => state.builder.widgets)
      const sqlParameters: SqlParameter[] = yield select((state: RootState) => state.builder.sqlParameters)
      const currentMap: Map = yield select((state: RootState) => state.maps.currentMap)

      sendParentAppEvent({
        type: 'cartoMapLoaded',
        data: {
          title: currentMap?.title ?? '',
          widgets: widgets.map(({ id, type, title }) => ({ id, type, title })),
          sqlParameters: sqlParameters.reduce(
            (result, sqlParameter) => {
              switch (sqlParameter.type) {
                case SqlParameterType.DateRange:
                case SqlParameterType.NumericRange:
                  result.push({ name: sqlParameter.start.sqlName, type: sqlParameter.type })
                  result.push({ name: sqlParameter.end.sqlName, type: sqlParameter.type })
                  break
                case SqlParameterType.Category:
                case SqlParameterType.Numeric:
                  result.push({ name: sqlParameter.item.sqlName, type: sqlParameter.type })
                  break
              }
              return result
            },
            [] as {
              name: string
              type: SqlParameterType
            }[]
          )
        }
      })
    }

    yield put(setIsLoading(false))
  } catch (e: any) {
    const apiError = createApiError(e)
    console.error('initBuilderMap failed', apiError.error)
    yield put(showGlobalBuilderError(`Loading map failed: ${apiError.error}`))
  }
}

function* snapshotMapConfig() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    let keplerMapConfig = currentMap.keplerMapConfig

    // we can receive empty configuration from map created from other sections like workflows
    if (!keplerMapConfig?.config) {
      keplerMapConfig = yield getMapConfig()
      // setCurrentMap cannot be called more than one as protection mechanism
      yield put(setCurrentMap(undefined))
      yield put(setCurrentMap({ ...currentMap, keplerMapConfig }))
    }

    // Snapshot initial map state, so URL parameters can later skip showing unchanged state. Use
    // JSON round-trip instead of structuredClone() for compatibility, for now.
    const initialConfig =
      keplerMapConfig && (JSON.parse(JSON.stringify(keplerMapConfig.config)) as CartoKeplerMapConfig)

    yield put(setCurrentMapInitialConfig(initialConfig))
  } catch (error) {
    console.error('Failed to initialize map config:', error)
    yield put(showGlobalBuilderError('Failed to initialize map configuration'))
  }
}

function* configureMapFromUrlParams({ searchParams }) {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const mapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    const isReadonly = builderMode === BuilderMode.Viewer || builderMode === BuilderMode.Public

    const defaultConfig: MapConfig = getDefaultMapConfigFromSearchParams(
      new URLSearchParams(searchParams || {}),
      currentMap.id
    )

    if (!defaultConfig) return

    const clonedMap: Map = JSON.parse(JSON.stringify(currentMap))

    setDefaultMapConfig(clonedMap, defaultConfig)

    if (isReadonly) {
      setViewerDefaultMapConfig(clonedMap, mapDatasets, defaultConfig)
    }

    if (isReadonly && defaultConfig?.search) {
      yield call(setViewerDefaultSearch, clonedMap, defaultConfig)
    }

    if (isReadonly && defaultConfig?.sqlParameters) {
      yield call(setViewerDefaultSqlParameters, clonedMap, mapDatasets, defaultConfig)
    }
    // setCurrentMap cannot be called more than one as protection mechanism
    yield put(setCurrentMap(undefined))
    yield put(setCurrentMap(clonedMap))
  } catch (error) {
    console.error('Failed to configure map from URL parameters:', error)
    yield put(showGlobalBuilderError('Failed to configure map from URL parameters'))
  }
}

function* maybeAutoLockCollaborativeMap() {
  try {
    const collaborativeMapsEnabled = yield select((state) => getFlag(state, FeatureFlag.CollaborativeMaps))

    const map = yield select((state) => state.maps.currentMap)
    if (!map.id) return

    const lockNeeded = Boolean(collaborativeMapsEnabled && map.collaborative)
    if (!lockNeeded) return true

    let currentMapStatus: BuilderLockState | undefined = map.lockStatus
    const currentUser = yield fetchQueryEffect(usersMeQuery)

    while (true) {
      const locked = currentMapStatus?.locked
      const lockedBySomeOneElse = locked && currentMapStatus?.userId !== currentUser.user_id
      if (lockedBySomeOneElse || currentMapStatus?.takingOver) {
        yield take([setCurrentMapLockStatus.type, updateCurrentMapLockStatus.type])
        currentMapStatus = yield select((state) => state.builder.currentMapLockStatus)
        continue
      }
      break
    }

    if (!currentMapStatus?.locked) {
      yield lockMap({ mapId: map.id })
    }
    yield put(setCurrentMapLockStatus({ locked: true, userId: currentUser.user_id }))
  } catch (e) {
    console.error('maybeAutoLockCollaborativeMap failed', e)
    throw e
  }
}

function* createNewMap(map: Omit<Map, 'id'>) {
  let quota: QuotaResult | undefined
  try {
    quota = yield call(getQuota, QuotaTypes.Maps)
    assertAddditionWouldNotExceedQuota(quota)

    map.keplerMapConfig = yield getNewMapConfig()
    const createdMap: Map = yield call(createMap, map)
    yield put(setCurrentMap(createdMap))
    yield put(setStatsStatus(RequestStatus.Idle))
    yield put(setLatestMapsStatus(RequestStatus.Idle))

    return createdMap
  } catch (e) {
    const apiError = createApiError(e)
    if (isHardQuotaError(apiError)) {
      apiError.quota = quota
      yield put(setStatsStatus(RequestStatus.Idle))
      yield put(setSaveMapError(apiError))
    } else {
      console.error('createNewMap failed', e)
      yield put(showGlobalBuilderError(apiError.error))
    }
    throw e
  }
}

export function* addLayer(
  action: PayloadAction<LayerRequest>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<any, any, any> {
  try {
    const layerRequest = { ...action.payload }
    const { type } = layerRequest

    const loader = getLayerLoader(layerRequest)

    if (!loader) {
      throw new Error(`Type ${type} not supported for addLayer`)
    }
    yield loader.add()
    yield call(ensureMapSaved, { immediate: true })
  } catch (error: any) {
    const msg = `Error at addLayer: ${error.message}`
    yield put(showGlobalBuilderError(msg))
    console.error(msg, error)
  }
}

function* removeWidgetsWithUnsupportedDatasets() {
  try {
    const incompatibleDataSources = yield select(getDataSourcesIncompatibleWithWidgets)
    const widgets = yield select((state) => state.builder.widgets)
    const widgetsToRemove = widgets.filter((widget) =>
      incompatibleDataSources.some((dataSource) => dataSource.id === widget.dataSource)
    )

    for (const widget of widgetsToRemove) {
      yield put(removeWidget(widget))
    }
  } catch (error: any) {
    console.error(error)
    yield put(showGlobalBuilderError(error.message))
  }
}

export function* handleAddLayerToExistingDataset(action: PayloadAction<string>) {
  try {
    const datasetId = action.payload
    const dataset: KeplerTable | undefined = yield select((state) => getkeplerDatasetById(state, datasetId))
    if (!dataset) return

    let newLayerLabel: string
    {
      const layerClasses = yield getDatasetSupportedLayerClasses({
        type: dataset.metadata.type,
        format: dataset.metadata.format
      })

      newLayerLabel = yield getNextLayerLabel()
      const layerConfig = findFirstSuitableLayerConfigByDataset({
        dataset,
        layerClasses,
        label: newLayerLabel
      })

      yield put(keplerAddLayerActionCreator({ ...layerConfig }))
    }

    {
      const layers: KeplerLayer[] = yield select((state) => state.keplerGl.map.visState.layers)
      const newlyAddedLayer = layers.find((layer) => layer.config.label === newLayerLabel)

      if (!newlyAddedLayer) return

      if (newlyAddedLayer.getNewLayerConfig) {
        yield put(layerConfigChange(newlyAddedLayer, newlyAddedLayer.getNewLayerConfig(dataset, layers)))
      }
    }
  } catch (error: any) {
    const msg = `Error at addLayer ${action.payload}. ${error.message}`
    yield put(showGlobalBuilderError(msg))
    console.error(msg, error)
  }
}

export function getLayerLoader(layerRequest: LayerRequest): LayerLoader {
  const { type } = layerRequest
  let loader

  switch (type) {
    case MapType.table:
    case MapType.tileset:
      loader = new TilesetLayerLoader(layerRequest)
      break

    case MapType.query:
      loader = new QueryLayerLoader(layerRequest)
      break

    case MapType.raster:
      loader = new LayerLoader(layerRequest)
      break
  }
  return loader
}

function* handleRefreshDataset(
  action: PayloadAction<string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<any, any, any> {
  try {
    const datasetId = action.payload
    if (!datasetId) {
      const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
      yield all(currentMapDatasets.map(({ id }) => call(refreshDataset, { datasetId: id })))
    } else {
      yield refreshDataset({ datasetId: action.payload })
    }
  } catch (e: any) {
    console.error('handleRefreshDataset', e)
    const msg = `Error loading the source ${action.payload}. ${e.message}`
    yield put(showGlobalBuilderError(msg))
    console.error(msg, e)
  }
}

// IMPORTANT! This function does not reset all properties, and is open to changes
// Theoretically, we should invoke getDefaultLayerConfig() from KeplerGL and use it here, but it does not work
function* resetLayerConfig(layer: KeplerLayer) {
  const resettedConfig = {
    ...layer.config,
    colorField: null,
    strokeColorField: null,
    sizeField: null,
    heightField: null,
    radiusField: null,
    customMarkersField: null,
    rotationField: null,
    strokeColorAggregation: null,
    radiusAggregation: null,
    sizeAggregation: null
  }

  // eslint-disable-next-line redux-saga/no-unhandled-errors
  yield put(layerConfigChange(layer, resettedConfig))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleAggregateDatasetByGeometry(
  action: PayloadAction<{ datasetId: string; layerId: string; geometry: string; aggregate: boolean }>
): Generator<any, any, any> {
  const { datasetId, layerId, aggregate, geometry } = action.payload

  try {
    yield put(setLayerPanelLoadingStatus(RequestStatus.Pending))

    const mapId: string | undefined = yield select((state: RootState) => state.maps.currentMap?.id)
    if (!mapId) return

    const layers = yield select((state: RootState) => state.keplerGl.map?.visState?.layers)
    const layer = layers.find((l) => l.id === layerId)
    const popupSettings = yield select((state: RootState) => state.builder.popupSettings)
    const widgets = yield select((state: RootState) => state.builder.widgets)

    const incompatibleSettings = getLayerTypeChangeIncompatibleSettings(layers, popupSettings, widgets, layerId)

    if (incompatibleSettings.length > 0) {
      const response = yield askQuestionWithDialog(LayerAggregateByGeometryConfirmDialogLazy, {
        warningElements: incompatibleSettings
      })

      if (response !== 'continue') {
        return
      }

      yield call(removeIncompatibleMapFeatures, { layer })
    }

    yield call(resetLayerConfig, layer)

    const dataset: MapDataset = yield select(getDatasetById, datasetId)
    const datasetRequest = {
      ...dataset,
      mapId,
      datasetId,
      provider: dataset.providerId,
      connection: dataset.connectionName
    }

    const loader = getLayerLoader(datasetRequest)
    yield loader.updateMapDataset({
      aggregationExp: aggregate ? DEFAULT_AGGREGATION_EXP : null
    })

    yield refreshDataset({ datasetId })

    const account = yield select((state) => state.account?.data)
    sendMetricEvent(MetricEventType.LayerAggregateByGeometry, {
      accountId: account.id,
      mapId,
      aggregate,
      geometry
    })
  } catch (error) {
    console.error('handleAggregateDatasetByGeometry error:', error)
    yield put(showGlobalBuilderError(String(error)))
  } finally {
    yield put(setLayerPanelLoadingStatus(RequestStatus.Idle))
  }
}

function* updateLayerType({
  mapId,
  datasetId,
  targetLayerType
}: {
  mapId: string
  datasetId: string
  targetLayerType: LayerType
}): Generator<any, any, any> {
  try {
    // eslint-disable-next-line redux-saga/no-unhandled-errors
    const dataset: MapDataset = yield select(getDatasetById, datasetId)
    // eslint-disable-next-line redux-saga/no-unhandled-errors
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)

    const datasetRequest = {
      ...dataset,
      mapId: mapId,
      datasetId,
      provider: dataset.providerId,
      connection: dataset.connectionName,
      datasetLabel: dataset.label,
      datasetColor: dataset.color,
      filters: cartoDataSources[datasetId]?.filters
    }

    yield getLayerLoader(datasetRequest).refreshDataset({ forceLayerType: targetLayerType, noCache: false })

    const account = yield select((state) => state.account?.data)
    sendMetricEvent(MetricEventType.LayerTypeChanged, {
      accountId: account.id,
      mapId: mapId,
      type: targetLayerType
    })
  } catch (e: any) {
    console.error('updateLayerType', e)
  }
}

export function* updateQuery({
  query,
  sqlParameters,
  format,
  provider,
  datasetId,
  mapId,
  geoColumn,
  queryAction = QueryActions.Update,
  skipSaveDataset,
  convertToQuery = false,
  createLayer = true
}: UpdateQueryProps) {
  try {
    const dataset: MapDataset = yield select(getDatasetById, datasetId)
    const oldKeplerDataset = yield select((state: RootState) => getkeplerDatasetById(state, datasetId))
    const oldSchema = getSchemaFromTilesetDataset(oldKeplerDataset)
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources || {})

    if (!convertToQuery && dataset.type !== MapType.query) {
      throw new Error(`Type ${dataset.type} not supported for updateQuery`)
    }
    if (!format) {
      format = dataset.format
    }
    if (!geoColumn) {
      geoColumn = dataset.geoColumn || null
    }
    if (!provider) {
      provider = dataset.providerId
    }
    if (sqlParameters === undefined) {
      sqlParameters = (yield select((state) => state.builder.sqlParameters)) as SqlParameter[]
    }

    const props = {
      ...dataset,
      mapId,
      datasetId,
      type: convertToQuery ? MapType.query : dataset.type,
      format,
      connection: dataset.connectionName,
      provider: dataset.providerId,
      source: dataset.source,
      geoColumn,
      datasetLabel: dataset.label,
      datasetColor: dataset.color,
      sqlParameters,
      filters: cartoDataSources[datasetId]?.filters,
      createLayer
    }

    switch (queryAction) {
      case QueryActions.Update: {
        yield new QueryLayerLoader(props).updateQuery({
          query,
          sqlParameters,
          format,
          provider,
          geoColumn,
          skipSaveDataset
        })

        const newKeplerDataset = yield select((state: RootState) => getkeplerDatasetById(state, datasetId))
        const newSchema = getSchemaFromTilesetDataset(newKeplerDataset)
        const schemaChanged = !datasetSchemaEqual(oldSchema, newSchema)

        if (schemaChanged) {
          // Dataset might have changed their columns, so we need to validate the Popup Settings
          yield updatePopupSettingsWithValidation(datasetId)

          // Dataset might have change, so we need to update the cross-filtering widgets
          yield updateWidgetsGivenDatasetChanges(datasetId)
        }

        break
      }
      case QueryActions.Cancel:
        yield new QueryLayerLoader(props).cancelQuery()
        break
      default:
        throw new Error(`Invalid action ${queryAction} for updateQuery`)
    }
  } catch (error: any) {
    const msg = `Error at updateQuery. ${error.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

function* cancellableAddQuery(action: PayloadAction<LayerRequest>) {
  try {
    yield race({
      queryTask: call(addLayer, action),
      cancelTask: take((action_) => {
        return action_.type === cancelQueryRequest.type && action.payload.source === action_.payload.query
      })
    })
  } catch (error) {
    console.error('cancellableAddQuery', error)
  }
}

function* cancellableUpdateQuery(action: PayloadAction<UpdateQueryProps>) {
  try {
    yield put(cancelQueryRequest({ datasetId: action.payload.datasetId }))

    yield race({
      queryTask: call(updateQuery, {
        ...action.payload,
        queryAction: QueryActions.Update
      }),
      cancelTask: take((action_) => {
        return action_.type === cancelQueryRequest.type && action_.payload?.datasetId === action.payload?.datasetId
      })
    })
  } catch (error) {
    console.error('cancellableUpdateQuery', error)
  }
}

function* cancelQuery(action: PayloadAction<UpdateQueryProps>) {
  yield updateQuery({
    ...action.payload,
    queryAction: QueryActions.Cancel
  })
}

function* checkDatasetCredentials() {
  try {
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const me: User = yield select((state) => state.users.me)

    if (shouldRequireViewerCredentialsForGuest(currentMapDatasets, me)) {
      yield put(setViewerCredentialsRequired([]))
      return false
    }

    const isGuest = me?.app_metadata?.roles?.includes(UserRole.Guest)
    if (isGuest) {
      return
    }

    if (!shouldRequireViewerCredentials({ builderMode, map: currentMap })) {
      return
    }

    const connections: Connection[] | undefined = yield fetchQueryEffect(connectionsQuery)

    const connectionsWithMissingCredentials = getConnectionsWithMissingViewerCredentials(
      currentMapDatasets,
      connections || []
    )

    if (connectionsWithMissingCredentials.length > 0) {
      yield put(setViewerCredentialsRequired(connectionsWithMissingCredentials))
      yield take(attachViewerCredentials.type)
      yield put(setViewerCredentialsRequired([]))
    }
  } catch (error: any) {
    if (error.message === 'Guest viewer requires credentials') {
      yield put(showGlobalBuilderError(error.message))
    } else {
      const msg = `Error at checkConnectionCredentials. ${error.message}`
      yield put(showGlobalBuilderError(msg))
    }
  }
}

function* assignTokensToDatasets() {
  try {
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    if (builderMode === BuilderMode.Editor) {
      return
    }

    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const me: User = yield select((state) => state.users.me)
    if (shouldRequireViewerCredentialsForGuest(currentMapDatasets, me)) {
      return false
    }
    const mapIsPublic = currentMap.privacy === MapPrivacy.Public
    const isNotGuest = !me?.app_metadata?.roles?.includes(UserRole.Guest)
    const isViewerMode = builderMode === BuilderMode.Viewer && isNotGuest
    let connections: Connection[] | undefined
    let user: User | undefined

    if (isViewerMode && !mapIsPublic) {
      ;[connections, user] = yield all([yield fetchQueryEffect(connectionsQuery), yield fetchQueryEffect(usersMeQuery)])
    }

    for (const dataset of currentMapDatasets) {
      const connection = connections?.find((conn) => conn.id === dataset.connectionId)
      const isConnectionAccessible =
        connection && user && (connection.user_id === user.user_id || connection.privacy === ConnectionPrivacy.Shared)

      const accessToken =
        mapIsPublic || !isConnectionAccessible || connection.carto_dw || !connection.viewerCredentials
          ? currentMap.token
          : undefined

      if (accessToken) {
        yield put(updateCurrentMapDataset({ id: dataset.id, dataset: { accessToken } }))
      }
    }
  } catch (error) {
    throw new Error(`markDatasetTokenUsage failed: ${error}`)
  }
}

function* setupDatasetsFreshness() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)

    const mapSettings = currentMap?.keplerMapConfig?.config?.mapSettings
    if (!mapSettings || !mapSettings.controlCacheTime || mapSettings.cacheTime === undefined) return

    const lastRefreshAt = Date.now() - mapSettings.cacheTime * 60 * 1000
    for (const dataset of currentMapDatasets) {
      yield put(updateCurrentMapDataset({ id: dataset.id, dataset: { lastRefreshAt } }))
    }
  } catch (error) {
    throw new Error(`markDatasetTokenUsage failed: ${error}`)
  }
}

export function* checkMapAuthorization({
  builderMode,
  searchParams
}: {
  builderMode: BuilderMode
  searchParams?: AnyUrlSearchParams
}) {
  try {
    if (builderMode === BuilderMode.Public) {
      return true
    }

    const map: Map = yield select((state) => state.maps.currentMap)
    const currentUser = yield fetchQueryEffect(usersMeQuery)

    if (builderMode === BuilderMode.Editor) {
      if (!userCanEditMap(map, currentUser)) {
        if (userCanViewMap(map, currentUser)) {
          navigateWithReload(`/viewer/${map.id}`, searchParams)
        } else {
          yield put(setFetchMapError({ status: 403, error: 'Unauthorized' }))
        }
        return false
      }
    } else {
      if (!userCanViewMap(map, currentUser)) {
        yield put(setFetchMapError({ status: 403, error: 'Unauthorized' }))
        return false
      }
    }
    return true
  } catch (e) {
    throw new Error(`checkMapAuthorization failed: ${e}`)
  }
}

export function* loadCurrentMap(options: AddDataToMapOptions) {
  try {
    yield checkDatasetCredentials()
    yield assignTokensToDatasets()
    yield migrateDocumentModeLayers()
    yield updateAggregationExpWithValidation()
    yield fixupDatasetsColorAndLabel()
    yield fixupBrokenColorRanges()
    yield setupDatasetsFreshness()

    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const me: User = yield select((state) => state.users.me)
    if (shouldRequireViewerCredentialsForGuest(currentMapDatasets, me)) {
      return false
    }

    yield loadPersistentUiStatePreLoad(currentMap)

    const mapSettings = currentMap?.keplerMapConfig?.config?.mapSettings
    const cacheMaxAgeInMinutes = mapSettings?.controlCacheTime ? mapSettings?.cacheTime : undefined
    const cacheControl = cacheMaxAgeInMinutes ? { 'Cache-Control': `max-age=${cacheMaxAgeInMinutes * 60}` } : undefined

    // Deep copy is necesary to avoid kepler error in parseSavedConfig function
    const mapConfig = KeplerGlSchema.parseSavedConfig(
      JSON.parse(JSON.stringify(currentMap?.keplerMapConfig || {}))
    ) as ParsedConfig

    // Empty filters cause error on loading related layers, so let's clean them up
    // if (mapConfig.visState?.filters.length) {
    //   mapConfig.visState.filters = mapConfig.visState.filters.filter((f) => f.name.length)
    // }

    // Remove filters from the old filter system. Now, for filtering we use c4r widgets instead of kepler filters
    // @ts-expect-error TODO
    mapConfig.visState.filters = []

    // Fix-up initial mapState to ensure we enforce minZoom to be BUILDER_MIN_FRACTIONAL_ZOOM
    if (!mapConfig.mapState) {
      mapConfig.mapState = NEW_MAP_STATE
    } else {
      mapConfig.mapState.minZoom = Number.isFinite(mapConfig.mapState.minZoom)
        ? Math.max(BUILDER_MIN_FRACTIONAL_ZOOM, mapConfig.mapState.minZoom as number)
        : BUILDER_MIN_FRACTIONAL_ZOOM
    }

    // First set the map configuration (viewport, basemap, etc.)
    yield put(receiveMapConfig(mapConfig, options))

    const widgets = currentMap.keplerMapConfig?.config?.widgets || []
    const originalFilters = currentMap.keplerMapConfig?.config?.filters
    const migratedFilters = migrateSpatialIndexFilters(originalFilters, {
      widgets
    })

    const filters = getValidatedFilters(migratedFilters, {
      widgets,
      mapDatasets: currentMapDatasets
    })

    // some datasets may be still loading when creating new map, filter them out
    const datasetsToLoad = currentMapDatasets.filter(
      (dataset) => dataset.status === undefined || dataset.status === RequestStatus.Idle
    )
    if (datasetsToLoad.length > 0) {
      yield all(
        datasetsToLoad.map((dataset) =>
          call(function* () {
            const datasetId = dataset.id
            try {
              const {
                providerId: provider,
                source,
                type,
                connectionName: connection,
                accessToken,
                format,
                geoColumn,
                columns,
                aggregationExp,
                aggregationResLevel,
                queryParameters,
                queryTemplate,
                color: datasetColor,
                label: datasetLabel
              } = dataset

              const datasetRequest = {
                mapId: currentMap.id,
                datasetId,
                provider,
                source,
                type,
                connection,
                columns,
                accessToken,
                format,
                geoColumn,
                aggregationExp,
                aggregationResLevel,
                datasetLabel,
                datasetColor,
                queryParameters,
                queryTemplate,
                ...(filters && { filters: filters[datasetId] })
              }

              const loader = getLayerLoader(datasetRequest)

              if (!loader) {
                yield put(
                  updateCurrentMapDataset({
                    id: datasetId,
                    dataset: {
                      status: RequestStatus.Rejected,
                      errorMessage: `Invalid type ${type}`
                    }
                  })
                )
                throw new Error(`Invalid type ${type}`)
              }
              const keplerDataset = yield loader.getKeplerDataset(cacheControl)
              const cartoData = yield loader.getCartoData()
              if (keplerDataset) {
                yield put(addDataToMap({ datasets: [keplerDataset], options }))
                yield loadDataForWidgets(cartoData, keplerDataset)
              }
            } catch (e: any) {
              yield put(
                updateCurrentMapDataset({
                  id: datasetId,
                  dataset: {
                    status: RequestStatus.Rejected,
                    errorMessage: `Failed to load dataset.`
                  }
                })
              )
              const msg = `Error loading the source ${dataset.label}. ${e.message}`
              yield put(showGlobalBuilderError(msg))
              console.error(msg, e)
            }
          })
        )
      )
    }

    yield loadAttributeStatsForDynamicTiling(currentMap, currentMapDatasets, cacheMaxAgeInMinutes)
    yield loadWidgetsMapConfig(currentMap)
    yield loadLegendSettings(currentMap)
    yield loadPopupSettings(currentMap)
    yield loadSqlParametersFromMap(currentMap)

    const aiSettings = currentMap.keplerMapConfig?.config?.aiSettings
    if (aiSettings) {
      yield put(updateAISettings(aiSettings))
    }

    yield loadPersistentUiStatePostLoad(currentMap)

    yield updateDatasetsColumns()
    yield addMissingLayersFromWorkflowDatasets()
    yield migrateGenAiToAgent()
  } catch (e: any) {
    console.error('loadCurrentMap failed', e)
    yield put(showGlobalBuilderError(e.message))
    throw new Error(`loadCurrentMap failed: ${e.message}`, { cause: e })
  }
}

export function* fixupDatasetsColorAndLabel() {
  try {
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)

    // color and label are actually used only in Editor mode, so we can skip the fixup in other modes

    if (builderMode !== BuilderMode.Editor) return

    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const datasets: MapDataset[] = yield select((state: RootState) => state.maps.currentMapDatasets)
    const newDatasets = [...datasets]
    for (let i = 0; i < newDatasets.length; i++) {
      const propsToUpdate: Partial<MapDataset> = {}
      const dataset = newDatasets[i]
      if (!dataset.color) {
        const color = getNewDatasetColor(newDatasets, i) as string
        propsToUpdate.color = color
      }
      if (!dataset.label) {
        const label = (yield getDatasetLabel(dataset.source, dataset.type)) as string
        propsToUpdate.label = label
      }

      if (Object.keys(propsToUpdate).length === 0) continue

      yield call(patchDataset, {
        mapId: currentMap.id,
        datasetId: dataset.id,
        body: propsToUpdate
      })
      yield put(
        updateCurrentMapDataset({
          id: dataset.id,
          dataset: propsToUpdate
        })
      )

      newDatasets[i] = { ...dataset, ...propsToUpdate }
    }
  } catch (error) {
    console.error('fixupDatasetsColorAndLabel', error)
  }
}

export async function fixupBrokenMapState() {
  try {
    const currentMap = appStoreSelect((state) => state.maps.currentMap)
    let mapState = currentMap?.keplerMapConfig?.config?.mapState
    if (!currentMap || !currentMap.keplerMapConfig || !mapState) return

    if (typeof mapState.latitude !== 'number' || !isLatitude(mapState.latitude)) {
      let latitude = Number(mapState.latitude)
      if (!isLatitude(latitude)) {
        latitude = NEW_MAP_STATE.latitude
      }
      mapState = {
        ...mapState,
        latitude
      }
    }
    if (typeof mapState.longitude !== 'number' || !isLongitude(mapState.longitude)) {
      let longitude = Number(mapState.longitude)
      if (!isLongitude(longitude)) {
        longitude = NEW_MAP_STATE.longitude
      }
      mapState = {
        ...mapState,
        longitude
      }
    }

    if (mapState === currentMap?.keplerMapConfig?.config?.mapState) return

    const newMap = {
      keplerMapConfig: {
        ...currentMap.keplerMapConfig,
        config: {
          ...currentMap.keplerMapConfig.config,
          mapState
        }
      }
    }
    appStoreDispatch(updateCurrentMap(newMap))
  } catch (error) {
    console.error('fixupBrokenMapState', error)
  }
}

export function* fixupBrokenColorRanges() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const layers = currentMap.keplerMapConfig?.config?.visState.layers || []
    if (!currentMap.keplerMapConfig?.config) return

    let updateNeeded = false
    const updatedLayers = layers.map((layer) => {
      for (const colorProp of ['colorRange', 'strokeColorRange']) {
        const colorRange = layer.config.visConfig[colorProp]
        if (!colorRange) continue

        const newColorRange = {
          ...colorRange,
          colors: colorRange.colors.map((color) => color.trim()),
          ...(colorRange.colorMap && {
            colorMap: colorRange.colorMap.map(([value, color]) => [value, color.trim()])
          })
        }
        if (isEqual(newColorRange, colorRange)) continue

        updateNeeded = true
        layer = {
          ...layer,
          config: {
            ...layer.config,
            visConfig: {
              ...layer.config.visConfig,
              [colorProp]: newColorRange
            }
          }
        }
      }
      return layer
    })

    if (updateNeeded) {
      const updatedMap = {
        keplerMapConfig: {
          ...currentMap.keplerMapConfig,
          config: {
            ...currentMap.keplerMapConfig.config,
            visState: {
              ...currentMap.keplerMapConfig.config.visState,
              layers: updatedLayers
            }
          }
        }
      }
      yield put(updateCurrentMap(updatedMap))
    }
  } catch (error) {
    console.error('fixupBrokenColorRanges', error)
  }
}

export function* updateAggregationExpWithValidation() {
  try {
    const datasets = yield select((state: RootState) => state.maps.currentMapDatasets)
    for (const dataset of datasets) {
      if (dataset.aggregationExp && dataset.aggregationExp.includes(',,')) {
        const updatedDataset = {
          ...dataset,
          aggregationExp: dataset.aggregationExp.replaceAll(',,', ',')
        }
        yield put(updateCurrentMapDataset({ id: dataset.id, dataset: updatedDataset }))
      }
    }
  } catch (error) {
    console.error('updateAggregationExpWithValidation', error)
  }
}

function* addMissingLayersFromWorkflowDatasets() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const layers = currentMap?.keplerMapConfig?.config?.visState.layers || []
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const mapState = yield select((state) => state.keplerGl.map.mapState)
    let lastDatasetId: string | undefined

    for (const dataset of currentMapDatasets) {
      if (!isWorkflowDataset(dataset)) continue
      if (layers.some((layer) => layer.config.dataId === dataset.id)) continue
      yield put(addLayerToExistingDatasetRequest(dataset.id))
      lastDatasetId = dataset.id
    }

    if (!lastDatasetId) return
    const keplerDataset: KeplerTable = yield select((state) => getkeplerDatasetById(state, lastDatasetId))

    if (!keplerDataset) return
    const [xmin, ymin, xmax, ymax] = getBoundsFromTilesetDataset(keplerDataset)
    const { latitude, longitude, zoom } = fitBounds({
      mapState,
      extent: {
        xmin,
        ymin,
        xmax,
        ymax
      }
    })
    yield put(
      updateMap({ zoom: clamp(zoom, mapState.minZoom, mapState.maxZoom || MAXIMUM_ZOOM_LEVEL), latitude, longitude })
    )
  } catch (e: any) {
    console.error('Error adding missing layers from workflow datasets:', e)
    yield put(showGlobalBuilderError(`Failed to add missing layers: ${e.message}`))
  }
}

function* handleRefreshDatasetsByConnections(action: PayloadAction<string[]>) {
  try {
    const connectionIds = action.payload
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)

    yield checkDatasetCredentials()

    const datasetsToRefresh = currentMapDatasets.filter(
      (dataset) => dataset.connectionId && connectionIds.includes(dataset.connectionId)
    )

    yield all(datasetsToRefresh.map((dataset) => call(refreshDataset, { datasetId: dataset.id })))
  } catch (error: any) {
    const msg = `Error at refreshing datasets. ${error.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

/**
 * Ensures map is saved without checking for autosaving.
 */
export function* ensureMapSaved(options?: { immediate?: boolean; silently?: boolean }) {
  try {
    // If a save is already in progress, wait until it's finished.
    if (!options?.immediate && (yield select((state) => !state.builder.saveNeeded))) {
      while ((yield select((state) => state.maps.mapEditStatus)) === RequestStatus.Pending) {
        yield take(setEditMapStatus.type)
      }
    }

    yield put(resetSaveNeeded())
    yield saveCurrentMap(options?.silently)
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

/**
 * Executed
 * * immediately on some actions to recheck if auto-saving has been reenabled
 * * debounced on `setSaveNeeded`
 */
export function* maybeExecutePendingAutosave() {
  try {
    const isAutosavingEnabled: boolean = yield select(isAutosavingEnabledSelector)
    const saveNeeded: boolean = yield select((state) => state.builder.saveNeeded)
    if (!isAutosavingEnabled || !saveNeeded) return

    yield ensureMapSaved()
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

/**
 * Mark save is needed if map is really changed.
 * @returns
 */
export function* checkIfSaveIsNeeded() {
  try {
    const isAutosavingEnabled: boolean = yield select(isAutosavingEnabledSelector)
    const isLoading: boolean = yield select((state) => state.builder.isLoading)
    const saveNeeded: boolean = yield select((state) => state.builder.saveNeeded)
    if (!isAutosavingEnabled || isLoading || saveNeeded) return

    const lastSavedConfig = yield select(
      (state: RootState) => state.maps.lastSavedMapConfig || state.maps.currentMapInitialConfig
    )
    const currentMapConfig = (yield select(mapConfigSelector))?.config

    if (lastSavedConfig && isEqualMapConfig(lastSavedConfig, currentMapConfig)) return

    yield put(setSaveNeeded())
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function isEqualMapConfig(a: unknown, b: unknown): boolean {
  // NOTE: Keys with values of 'undefined' in current config may be missing
  // in the last saved config. _.isMatch considers these different, so use
  // JSON stringify-parse round trip to remove those keys.
  a = JSON.parse(JSON.stringify(a))
  b = JSON.parse(JSON.stringify(b))
  return isEqual(a, b)
}

function* centerMapOnRaster(rasterMetadata: RasterMetadata) {
  try {
    if (rasterMetadata.bounds) {
      // bounds+minresolution based center/zoom seems more accurate than
      const bounds = rasterMetadata.bounds
      const [latitude, longitude] = [(bounds[1] + bounds[3]) / 2, (bounds[0] + bounds[2]) / 2]
      const zoom = rasterMetadata.minresolution
      yield put(updateMap({ zoom, latitude, longitude }))
    } else if (rasterMetadata.center) {
      // fallback to center based location (which is sometimes overzoomed, that's it's a fallback)
      const [longitude, latitude, zoom] = rasterMetadata.center
      yield put(updateMap({ zoom, latitude, longitude }))
    }
  } catch (e: any) {
    console.error('centerMapOnRaster failed', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* handleAddDataToMap(action: PayloadAction<AddDataToMapPayload>) {
  try {
    const options = action.payload.options
    const dataset = action.payload.datasets[0]
    const metadata = dataset?.metadata as KeplerTableMetadata | undefined
    if (!dataset || !metadata || !options) return

    // Center map on raster data
    // Overrides Kepler's builtin mechanism which doesn't support min/max zoom which are critical to rasters
    // as in some viewports, calculated zoom level may be outside of rendered range
    if (options.centerMap && metadata.type == MapType.raster && metadata.raster_metadata) {
      const rasterMetadata = metadata.raster_metadata
      yield centerMapOnRaster(rasterMetadata)
    }
  } catch (e: any) {
    console.error('handleAddDataToMap failed', e)
    yield put(showGlobalBuilderError(e.message))
  }
}

function* showGlobalError(action: PayloadAction<string>) {
  const message = action.payload
  if (!message) {
    console.error('showGlobalError invoked without message')
    return
  }

  try {
    yield put(addNotification(errorNotification({ message })))
  } catch (e: any) {
    console.error(e)
  }
}

function* removeStoredDataset(action: PayloadAction<{ dataId: string }> | { dataId: string }) {
  try {
    const map: Map = yield select((state) => state.maps.currentMap)
    const mapId = map.id

    const datasetId =
      (action as { dataId: string }).dataId || (action as PayloadAction<{ dataId: string }>).payload.dataId

    yield call(removeCrossFilteringWidgetFilters, datasetId)
    yield put(deleteCurrentMapDataset(datasetId))
    yield call(removeDataForWidgets, datasetId)
    if (!isAgentDataset(datasetId)) {
      yield call(deleteMapDataset, { datasetId, mapId })
    }
    yield call(updateCrossFilteringWidgets)
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

// Old dataset is a table dataset that doesn't have the geomColumn persisted in backend
// We should keep this function until all table datasets have geomColumn
function* addMissingPropertiesToOldDatasets() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const mapId = currentMap.id

    if (!mapId || currentMapDatasets.length === 0) {
      return
    }
    yield all(
      currentMapDatasets.map((dataset) =>
        call(function* () {
          if (dataset.type === MapType.table && !dataset.geoColumn) {
            const tableObject: DataExplorerTable = yield call(getDataExplorerObject, {
              link: `/connections/${dataset.connectionId}/resources/${dataset.source}?type=${TreeObjectType.Table}`
            })
            const { geoColumn } = getGeoColumnFromTable(tableObject)
            yield call(patchDataset, {
              mapId,
              datasetId: dataset.id,
              body: { geoColumn, format: Formats.TILEJSON }
            })
            yield put(
              updateCurrentMapDataset({
                id: dataset.id,
                dataset: { geoColumn }
              })
            )
          }
        })
      )
    )
  } catch (e: any) {
    // We do not do anything becasuse we do not block the map load if this method fails
    console.warn('it has not been possible to update all the old dataset')
  }
}

function* addLayerFromImportedJob(action: PayloadAction<ImportingJob>) {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const { mapId, connection, destination, name, createLayerAfterJobImportedForMap } = action.payload

    if (mapId && currentMap.id === mapId) {
      const connectionData: Connection = yield select((state) =>
        state.connections.myConnections.find((c) => c.name === connection)
      )
      const tableObject: DataExplorerTable = yield call(getDataExplorerObject, {
        link: `/connections/${connectionData.id}/resources/${destination}.${name}?type=${TreeObjectType.Table}`
      })

      const { type, format, source, geoColumn } = getDatasetSettingsForTable({ table: tableObject })

      let columns: string[] | undefined
      if (datasetRequiresColumns({ type, format, geoColumn })) {
        columns = getDatasetDefaultColumns({ geoColumn, schema: tableObject.schema })
      }

      yield put(
        addLayerRequest({
          mapId,
          type,
          format,
          geoColumn,
          columns,
          source,
          connection: connection,
          provider: connectionData.provider_id,
          createLayer: createLayerAfterJobImportedForMap
        })
      )

      sendMetricEvent(MetricEventType.DataSourceAdded, {
        mapId,
        type: 'single',
        createLayer: !!createLayerAfterJobImportedForMap
      })
    }
  } catch (e) {
    // TODO
  }
}

export function* switchLayerTypeWithDatasetChange({
  layerId,
  newLayerType
}: {
  layerId: string
  newLayerType: LayerType
}) {
  try {
    const layerToUpdate = yield select(getLayerById, layerId)
    const datasetId = layerToUpdate?.config?.dataId

    if (!layerToUpdate || !datasetId) throw new Error('layer or dataset not found')

    yield runDatasetTransaction(datasetId, switchLayerTypeWithDatasetChangeInt, {
      layerId,
      newLayerType,
      datasetId,
      layerToUpdate
    })
  } catch (error) {
    yield put(showGlobalBuilderError(String(error)))
  }
}

function* switchLayerTypeWithDatasetChangeInt({
  layerId,
  newLayerType,
  datasetId,
  layerToUpdate
}: {
  layerId: string
  newLayerType: LayerType
  datasetId: string
  layerToUpdate: Layer
}) {
  try {
    const oldLayerType = layerToUpdate.type
    if (newLayerType === oldLayerType) return

    const mapId: string | undefined = yield select((state) => getCurrentMap(state)?.id)
    if (!mapId) throw new Error('layer or dataset not found')

    if (!(yield call(layerTypeChangesIncompatibleUserPrompt, { layerId, changeType: newLayerType }))) {
      return
    }
    yield put(setLayerUpdateStatus({ layerId, status: RequestStatus.Pending }))

    // @ts-expect-error TODO
    yield call(removeIncompatibleMapFeatures, { layer: layerToUpdate })

    const mapDataset: MapDatasetEnriched | undefined = yield select((state) => getEnrichedDatasetById(state, datasetId))
    if (!mapDataset) throw new Error('dataset not found')

    let update: Partial<MapDataset> = {}

    if (
      newLayerType === LayerTypes.Quadbin ||
      newLayerType === LayerTypes.H3 ||
      newLayerType === LayerTypes.HeatmapTile ||
      newLayerType === LayerTypes.ClusterTile
    ) {
      const oldGeoColumn = mapDataset?.geoColumn || DEFAULT_GEO_COLUMN
      const aggregationLevelByLayerType = {
        [LayerTypes.H3]: 5,
        [LayerTypes.Quadbin]: 5,
        [LayerTypes.HeatmapTile]: DEFAULT_AGGREGATION_LEVEL_HEATMAP,
        [LayerTypes.ClusterTile]: DEFAULT_AGGREGATION_LEVEL_CLUSTER
      }
      const spatialIndexColumnByLayerType = {
        [LayerTypes.H3]: SpatialIndex.H3,
        [LayerTypes.Quadbin]: SpatialIndex.QUADBIN,
        [LayerTypes.HeatmapTile]: SpatialIndex.QUADBIN,
        [LayerTypes.ClusterTile]: SpatialIndex.QUADBIN
      }

      update = {
        geoColumn: getGeoColumnSpecifier(spatialIndexColumnByLayerType[newLayerType], oldGeoColumn),
        aggregationExp: getDefaultAggregationExpForLayerType(
          newLayerType,
          mapDataset.providerId,
          mapDataset.metadata?.schema
        ),
        aggregationResLevel: aggregationLevelByLayerType[newLayerType] || 5,
        columns: null
      }
    } else {
      const oldGeoColumn = mapDataset?.geoColumn || DEFAULT_GEO_COLUMN
      const newGeoColumn = getGeoColumnSpecifier(NoSpatialIndex, oldGeoColumn)

      const keplerDataset: KeplerTable = yield select((state) => getkeplerDatasetById(state, datasetId))
      const columns = datasetRequiresColumns({
        ...mapDataset,
        geoColumn: newGeoColumn
      })
        ? getDatasetDefaultColumns({ geoColumn: newGeoColumn, schema: keplerDataset?.metadata?.schema })
        : null
      update = {
        geoColumn: newGeoColumn,
        aggregationExp: null,
        aggregationResLevel: null,
        columns
      }
    }

    yield put(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: update
      })
    )

    yield updateLayerType({ mapId, datasetId, targetLayerType: newLayerType })
    yield call(patchDataset, {
      mapId,
      datasetId,
      body: update
    })

    yield put(layerTypeChange(layerToUpdate, newLayerType))
  } catch (error: any) {
    yield put(showGlobalBuilderError(String(error)))
  } finally {
    yield put(setLayerUpdateStatus({ layerId, status: RequestStatus.Idle }))
  }
}

export function* handleQueryThisTable(action: PayloadAction<{ datasetId: string }>) {
  try {
    const { datasetId } = action.payload
    const mapId: string | undefined = yield select((state: RootState) => state.maps.currentMap?.id)
    if (!mapId) {
      return
    }
    const incompatibleSettings: string[] = yield select((state) =>
      getConvertDatasetToQueryIncompatibleSettings(state, datasetId)
    )

    if (incompatibleSettings.length > 0) {
      const response = yield askQuestionWithDialog(LayerTypeChangeConfirmDialogLazy, {
        changeType: 'query',
        elements: incompatibleSettings
      })

      if (response !== 'continue') {
        return
      }
    }
    yield put(showSQLPanel({ datasetId, mapId, minimized: false, query: '' }))
  } catch (error) {
    yield put(showGlobalBuilderError(String(error)))
  }
}

export function* handleConvertTableToQueryDataset(
  action: PayloadAction<{ datasetId: string; sqlQuery: string; geoColumn: string }>
) {
  let updatedLayers: Layer[] = []
  try {
    const { datasetId, sqlQuery, geoColumn } = action.payload
    const mapId: string | undefined = yield select((state: RootState) => state.maps.currentMap?.id)
    if (!datasetId || !mapId) {
      throw new Error('layer or dataset not found')
    }
    const allLayersBeforeUpdate: KeplerLayer[] = yield select((state: RootState) => state.keplerGl.map.visState.layers)

    const keplerDataset: KeplerTable = yield select((state) => getkeplerDatasetById(state, datasetId))
    const mapDataset: MapDataset = yield select((state) =>
      state.maps.currentMapDatasets.find((d) => d.id === datasetId)
    )

    const updatedLayerIndices = allLayersBeforeUpdate.reduce((indices, layer, index) => {
      if (layer.config.dataId === datasetId && !isLayerTypeCompatibleWithQuery(layer.type)) {
        indices.push(index)
      }
      return indices
    }, [] as number[])

    updatedLayers = updatedLayerIndices.map((index) => allLayersBeforeUpdate[index])

    for (const layer of updatedLayers) {
      yield put(setLayerUpdateStatus({ layerId: layer.id, status: RequestStatus.Pending }))
    }

    const columns = datasetRequiresColumns({
      ...mapDataset,
      type: MapType.query,
      format: Formats.TILEJSON,
      geoColumn
    })
      ? getDatasetDefaultColumns({ geoColumn, schema: keplerDataset?.metadata?.schema })
      : null

    yield updateQuery({
      mapId,
      datasetId,
      query: sqlQuery,
      format: Formats.TILEJSON,
      geoColumn,
      columns,
      convertToQuery: true
    })

    // get layers again as `layerTypeChange` changes `id` of layers
    const allLayerAfterUpdate: Layer[] = yield select((state: RootState) => state.keplerGl.map.visState.layers)

    for (const layerIdx of updatedLayerIndices) {
      const oldLayerId = allLayersBeforeUpdate[layerIdx]?.id
      const newLayerId = allLayerAfterUpdate[layerIdx]?.id
      if (oldLayerId && newLayerId) {
        yield put(moveLayerPopupSettings({ oldLayerId, newLayerId }))
      }
    }
  } catch (error) {
    yield put(showGlobalBuilderError(String(error)))
  } finally {
    for (const layer of updatedLayers) {
      yield put(setLayerUpdateStatus({ layerId: layer.id, status: RequestStatus.Idle }))
    }
  }
}

export function* handleDuplicateDataset(action: PayloadAction<{ datasetId: string }>) {
  try {
    const { datasetId } = action.payload
    const mapId: string | undefined = yield select((state: RootState) => state.maps.currentMap?.id)
    if (!mapId) {
      throw new Error('Map not found')
    }

    const sqlParameters = yield select((state) => state.builder.sqlParameters)
    // Get the original dataset
    const originalDataset: MapDataset = yield select((state) =>
      state.maps.currentMapDatasets.find((d) => d.id === datasetId)
    )
    if (!originalDataset) {
      throw new Error('Dataset not found')
    }

    // Create a layer request for the duplicated dataset
    const layerRequest = {
      mapId,
      type: originalDataset.type,
      source: originalDataset.queryTemplate || originalDataset.source,
      format: originalDataset.format,
      geoColumn: originalDataset.geoColumn,
      columns: originalDataset.columns,
      connection: originalDataset.connectionName,
      provider: originalDataset.providerId,
      queryParameters: originalDataset.queryParameters,
      createLayer: false,
      sqlParameters: sqlParameters
    }

    yield call(addLayer, addLayerRequest(layerRequest))
  } catch (error: any) {
    const msg = `Error duplicating dataset: ${error.message}`
    yield put(showGlobalBuilderError(msg))
    console.error(msg, error)
  }
}

function* syncDatasetsColumnsAfterVisConfigChange(action: PayloadAction<{ oldLayer: Layer }>) {
  try {
    // TODO: why action sometimes has no payload ?
    const datasetId = action.payload?.oldLayer?.config?.dataId || (action as any).oldLayer?.config?.dataId
    if (!datasetId) return

    yield updateDatasetColumnsFromMap(datasetId)
  } catch (e: any) {
    console.error('syncDatasetsColumnsAfterVisConfigChange', e)
    const msg = `Error updating columns. ${e.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

function* storeUrlParameters() {
  try {
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    const previewMode: boolean = yield select((state) => state.builder.previewMode)
    if ((builderMode !== BuilderMode.Viewer && builderMode !== BuilderMode.Public) || previewMode) return

    const initialConfig: CartoKeplerMapConfig = yield select((state) => state.maps.currentMapInitialConfig)
    const initialMapState = initialConfig?.mapState
    if (!initialConfig || !initialMapState) return

    // [sc-501705] AI Agents may create temporary sources, layers, and more. To
    // avoid many special cases, prevent saving URL parameters during AI threads.
    const genAiThread = yield select((state) => state.builder.genAiThread)
    if (genAiThread) return

    const url = new URL(location.href)
    const { mapState, visState } = yield select((state) => state.keplerGl.map)
    const { widgets, filters, spatialFilter }: WidgetsMapConfig = yield select(widgetsMapConfigSelector)
    const searchText: string = yield select((state) => state.builder.geocoderSearchText)

    // Lat/lng
    if (
      !isEqual(initialMapState?.latitude, mapState.latitude) ||
      !isEqual(initialMapState?.longitude, mapState.longitude)
    ) {
      url.searchParams.set(BuilderSearchParam.LATITUDE, mapState.latitude.toFixed(6))
      url.searchParams.set(BuilderSearchParam.LONGITUDE, mapState.longitude.toFixed(6))
    } else {
      url.searchParams.delete(BuilderSearchParam.LATITUDE)
      url.searchParams.delete(BuilderSearchParam.LONGITUDE)
    }

    // Zoom
    if (!isEqual(initialMapState?.zoom, mapState.zoom)) {
      url.searchParams.set(BuilderSearchParam.ZOOM, mapState.zoom.toFixed(0))
    } else {
      url.searchParams.delete(BuilderSearchParam.ZOOM)
    }

    // Pitch
    if (mapState.pitch && !isEqual(initialMapState?.pitch, mapState.pitch)) {
      url.searchParams.set(BuilderSearchParam.PITCH, mapState.pitch.toFixed(2))
    } else {
      url.searchParams.delete(BuilderSearchParam.PITCH)
    }

    // Bearing
    if (mapState.bearing && !isEqual(initialMapState?.bearing, mapState.bearing)) {
      url.searchParams.set(BuilderSearchParam.BEARING, mapState.bearing.toFixed(2))
    } else {
      url.searchParams.delete(BuilderSearchParam.BEARING)
    }

    const parentAppEvent: ParentAppMapUpdatedEventData = {
      lat: mapState.latitude,
      long: mapState.longitude,
      zoom: mapState.zoom,
      pitch: mapState.pitch,
      bearing: mapState.bearing,
      sqlParametersValues: [],
      widgetFilters: [],
      layers: []
    }

    const spatialFilterWkt = spatialFilter && safeWriteWKT(spatialFilter.geometry as GeoJSON.Geometry)
    if (spatialFilterWkt) {
      parentAppEvent.mask = spatialFilterWkt
    }
    // Spatial filter
    if (spatialFilterWkt && !isEqual(initialConfig?.spatialFilter, spatialFilter)) {
      url.searchParams.set(BuilderSearchParam.SPATIAL_FILTER, spatialFilterWkt)
    } else {
      url.searchParams.delete(BuilderSearchParam.SPATIAL_FILTER)
    }

    // SQL parameters
    const initialSqlParameters = initialConfig?.sqlParameters || []
    const sqlParameters: SqlParameter[] = yield select((state) => state.builder.sqlParameters)
    for (const parameter of sqlParameters) {
      const initialParameter = initialSqlParameters.find(({ id }) => id === parameter.id)
      const isEdited = !isEqual(initialParameter, parameter)

      switch (parameter.type) {
        case SqlParameterType.Category: {
          parentAppEvent.sqlParametersValues.push({ name: parameter.item.sqlName, value: parameter.item.value })

          const value = parameter.item.value.join(',')
          if (isEdited) {
            url.searchParams.set(`param_${parameter.item.sqlName}`, value)
          } else {
            url.searchParams.delete(`param_${parameter.item.sqlName}`)
          }
          break
        }

        case SqlParameterType.Numeric:
          parentAppEvent.sqlParametersValues.push({
            name: parameter.item.sqlName,
            value: parameter.item.value
          })
          if (isEdited) {
            url.searchParams.set(`param_${parameter.item.sqlName}`, parameter.item.value.toString())
          } else {
            url.searchParams.delete(`param_${parameter.item.sqlName}`)
          }
          break

        case SqlParameterType.DateRange:
        case SqlParameterType.NumericRange:
          parentAppEvent.sqlParametersValues.push(
            {
              name: parameter.end.sqlName,
              value: String(parameter.end.value)
            },
            {
              name: parameter.start.sqlName,
              value: String(parameter.start.value)
            }
          )
          if (isEdited) {
            url.searchParams.set(`param_${parameter.start.sqlName}`, parameter.start.value.toString())
            url.searchParams.set(`param_${parameter.end.sqlName}`, parameter.end.value.toString())
          } else {
            url.searchParams.delete(`param_${parameter.start.sqlName}`)
            url.searchParams.delete(`param_${parameter.end.sqlName}`)
          }
          break
      }
    }

    // Filters
    const initialFilters = initialConfig?.filters || {}
    for (const widget of widgets) {
      const dataset: MapDataset = yield select(getDatasetById, widget.dataSource)
      const filter = getFilterByWidget(widget, dataset, filters)
      const initialFilter = getFilterByWidget(widget, dataset, initialFilters)
      if (!filter || isEqual(initialFilter?.values, filter.values)) {
        url.searchParams.delete(`widget_${widget.id}`)
        continue
      }

      switch (widget.type) {
        case WidgetType.CATEGORY:
        case WidgetType.PIE: {
          const value = filter.values.join(',')
          url.searchParams.set(`widget_${widget.id}`, value)
          parentAppEvent.widgetFilters.push({
            id: widget.id,
            value: filter.values as string[]
          })
          break
        }

        case WidgetType.HISTOGRAM:
        case WidgetType.RANGE: {
          const value = filter.values.map((range) => range.join('-')).join(',')
          url.searchParams.set(`widget_${widget.id}`, value)
          parentAppEvent.widgetFilters.push({
            id: widget.id,
            value: filter.values as number[][]
          })
          break
        }
        default:
          url.searchParams.delete(`widget_${widget.id}`)
          break
      }
    }

    // Layers
    const initialLayers = initialConfig?.visState.layers || []
    const layerIdToIndex = Object.fromEntries(initialLayers.map((layer, idx) => [layer.id, idx]))
    const visibleLayerIndices = visState.layers
      ?.map((layer) => layer.config.isVisible && layerIdToIndex[layer.id])
      .filter(Number.isInteger)
      .sort()
    const initialLayerIndices = initialLayers
      .map((layer) => layer.config.isVisible && layerIdToIndex[layer.id])
      .filter(Number.isInteger)
      .sort()
    parentAppEvent.layers = visibleLayerIndices
    if (visibleLayerIndices?.length && !isEqual(initialLayerIndices, visibleLayerIndices)) {
      url.searchParams.set(BuilderSearchParam.LAYERS, visibleLayerIndices.sort().join(','))
    } else {
      url.searchParams.delete(BuilderSearchParam.LAYERS)
    }

    // Search
    if (searchText) {
      parentAppEvent.search = searchText
      url.searchParams.set(BuilderSearchParam.SEARCH, searchText)
    } else {
      url.searchParams.delete(BuilderSearchParam.SEARCH)
    }

    if (url.href.length > MAX_URL_LENGTH) {
      for (const param in BuilderSearchParam) {
        url.searchParams.delete(BuilderSearchParam[param])
      }
    }

    history.replaceState(null, '', url.pathname + url.search)

    sendParentAppEvent({
      type: 'cartoMapUpdated',
      data: parentAppEvent
    })
  } catch (e) {
    console.error('storeUrlParameters', e)
  }
}

function* watchMap() {
  yield takeEvery(initBuilderMap.type, handleInitBuilderMap)
  yield takeEvery(addLayerRequest.type, addLayer)
  yield takeEvery(addLayerToExistingDatasetRequest.type, handleAddLayerToExistingDataset)
  yield takeEvery(addQueryRequest.type, cancellableAddQuery)
  yield takeEvery(updateQueryRequest.type, cancellableUpdateQuery)
  yield takeEvery(cancelQueryRequest.type, cancelQuery)
  yield takeEvery(ActionTypes.ADD_DATA_TO_MAP, handleAddDataToMap)
  yield takeEvery(showGlobalBuilderError.type, showGlobalError)
  // @ts-expect-error TODO
  yield takeEvery(ActionTypes.REMOVE_DATASET, removeStoredDataset)
  yield takeEvery(addLayerFromImportedJobRequest.type, addLayerFromImportedJob)
  yield takeEvery(refreshDatasetRequest.type, handleRefreshDataset)
  yield takeEvery(refreshDatasetsByConnections.type, handleRefreshDatasetsByConnections)
  yield takeEvery(queryThisTableRequest.type, handleQueryThisTable)
  yield takeEvery(convertTableToQueryDatasetRequest.type, handleConvertTableToQueryDataset)
  yield takeEvery(aggregateDatasetByGeometry.type, handleAggregateDatasetByGeometry)
  yield takeEvery(duplicateDatasetRequest.type, handleDuplicateDataset)
  yield takeEvery(ActionTypes.REMOVE_LAYER, removeWidgetsWithUnsupportedDatasets)

  yield takeEvery(
    [
      ActionTypes.LAYER_VISUAL_CHANNEL_CHANGE,
      ActionTypes.LAYER_COLOR_UI_CHANGE,
      ActionTypes.LAYER_VIS_CONFIG_CHANGE,
      ActionTypes.LAYER_TEXT_LABEL_CHANGE,
      ActionTypes.LAYER_CONFIG_CHANGE
    ],
    syncDatasetsColumnsAfterVisConfigChange
  )

  // these actions may reenable autosaving
  yield takeLatest(
    [
      setIsLoading.type,
      setBuilderMode.type,
      setPreviewMode.type,
      updateCurrentMapDataset.type,
      addTimeSeriesDataSourceAnimating.type,
      removeTimeSeriesDataSourceAnimating.type
    ],
    maybeExecutePendingAutosave
  )
  try {
    // debounce saveNeeded check only a little, so we don't re-check it during animations
    yield debounce(50, actionsListForAutoSaving, checkIfSaveIsNeeded)

    // debounce actual save 1 second to ensure user finished "chunk" of editing
    yield debounce(1000, setSaveNeeded.type, maybeExecutePendingAutosave)

    yield debounce(5000, actionsListForCreateThumbnail, createMapThumbnail)
    yield takeEvery(setMapScreenshot.type, saveMapThumbnail)
    yield debounce(1000, actionsListForUrlUpdate, storeUrlParameters)
  } catch (e: any) {
    // TODO
  }
}

export default function* builderSaga() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
